import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, Select, Switch } from '@/components/ui';
import { getSettings, updateSettings, type WorkspaceSettings as WS } from '@/data/client';
import { FormError } from '@/components/crm/Modal';

const ZONES: string[] = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone')
  ?? ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'UTC'];

export function WorkspaceSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['settings'], queryFn: getSettings });
  const [f, setF] = useState<WS | null>(null);
  useEffect(() => { if (data) setF(data); }, [data]);
  const save = useMutation({
    mutationFn: () => updateSettings(f!),
    onSuccess: () => { for (const k of ['settings', 'me', 'audit']) void qc.invalidateQueries({ queryKey: [k] }); },
  });
  if (!f) return null;
  const dirty = JSON.stringify(f) !== JSON.stringify(data);

  return (
    <Card padding={false} className="crm-section crm-settings-card">
      <div className="crm-form">
        <Input label="Workspace name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <div className="crm-grid2">
          <Select label="Time zone" hint="Working hours for scheduling and the day boundary for reports." options={ZONES} value={f.timezone}
            onChange={(e) => setF({ ...f, timezone: e.target.value })} />
          <Select label="Default attribution" hint="Which lead's source reports use. Both are always stored."
            options={[{ value: 'first_touch', label: 'First touch (recommended)' }, { value: 'last_touch', label: 'Last touch' }]}
            value={f.defaultAttribution} onChange={(e) => setF({ ...f, defaultAttribution: e.target.value as WS['defaultAttribution'] })} />
        </div>
        <Switch label="Track gross margin on deals" checked={f.trackMargin} onChange={(e) => setF({ ...f, trackMargin: e.target.checked })} />
        <p className="crm-cell-sub">Adds a margin % field to deals and turns revenue reports into profit reports. Turning it off hides the field; values already entered are kept.</p>
        <FormError error={save.error} />
        <div className="crm-record__actions">
          <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>{save.isPending ? 'Saving…' : 'Save changes'}</Button>
          {save.isSuccess && !dirty && <span className="crm-cell-sub">Saved.</span>}
        </div>
      </div>
    </Card>
  );
}
